import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	ParentBoundComponent,
	Module,
	Form,
	FormItem,
	TextInput,
	LookupInput,
	Button,
	TabPane,
	showMessageBox,
	webapi,
	REPORTED_EXCEPTION,
	Icon,
	DateUtils,
	updateEntityTimestamp,
	FlexBox,
	FlexBoxBlock
} from 'ps-gui';

import {
	getFormItemLabel,
	getPhrase
} from '../data/phrases';
import SubmissionModel, {
	getDetailTabHeader,
	getRoleTypeId,
	STATUS_GROUPS
} from './submissionmodel';
import {
	CellMainText,
	CellSubText
} from './portalresultsetmodule';
import { renderDateTimeCell } from './commoncellrenderers';
import { getLookup } from '../data/lookup';
import {
	ModuleBodySection,
	ModuleHeaderSection,
	ModuleHeader,
	ModuleHeaderBlock
} from '../blocks/modulesections';
import { ReactComponent as ModuleLogo } from '../svg/submission.svg';
import memoizeOne from 'memoize-one';
import UploadPane from '../components/uploadpane';
import {
	downloadFile,
	formatFileSize,
	getGlobalContextChangeChecker
} from './utils';
import CheckButton from '../components/checkbutton';
import {
	Dialog,
	DialogButtons,
	DialogContent,
	DialogHeader,
	DialogSubtext
} from '../components/dialog';
import {
	Buttons,
	Label,
	ReadOnlyDetail,
	StatusBubble,
	StatusChangeHistory
} from './submissioncomponents';
import FileList from '../components/filelist';
import { withGlobalContext } from '../data/globalcontext';

const Wrapper = styled.div`
`;

const Content = styled.div`
	margin-top: 24px;
`;

const FormWrapper = styled.div`
	max-width: 700px;
	.ps-formItem {
		margin-bottom: 24px;
	}
`;

const RowCountEtc = styled.div`
	margin-bottom: 24px;
`;

const RowCount = styled.div`
	display: inline-block;
	color: ${props => props.theme.disabledTextColor};
	font-size: 20px;
	margin-right: 40px;
	.label {
		font-weight: 400;
		margin-right: 5px;
	}
	.value {
		font-weight: 900;
	}
`;

const TabText = styled.span`

	.icon {
		position: relative;
		top: -1px;
	}

	.icon.left {
		margin-right: 8px;
	}

	.icon[data-name=close] {
		margin-left: 16px;
		* {
			fill: #4FC098;
		}
	}
`;

const QuickFilterHeader = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
	margin-bottom: 8px;
`;

const QuickFilter = styled.div`
	margin-bottom: 24px;
	.checkButton {
		margin-right: 16px;
	}
`;

const DialogSubmission = styled.div`
	position: relative;
	.createdWhen {
		color: #575757CC;
		position: absolute;
		right: 0px;
		top: 0px;
		font-weight: 300;
		font-size: 14px;
		line-height: 30px;
	}

	.type {
		padding-right: 80px;
		font-weight: 400;
		font-size: 16px;
		line-height: 24px;
	}

	.name {
		font-weight: 500;
		font-size: 16px;
		line-height: 30px;
		color: #575757;
		margin-right: 16px;
		display: inline-block;
	}
`;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);

const singleFileSizeLimit = 20 * 1024 * 1024;
const totalFileSizeLimit = 300 * 1024 * 1024;

function toMB(bytes) {
	return bytes / 1024 / 1024;
}

class SubmissionModule extends ParentBoundComponent {

	constructor() {
		super();

		this.setName = this.setName.bind(this);
		this.setRoleId = this.setRoleId.bind(this);
		this.setTypeId = this.setTypeId.bind(this);
		this.setComments = this.setComments.bind(this);
		this.onTabSelected = this.onTabSelected.bind(this);
		this.submit = this.submit.bind(this);
		this.saveForLater = this.saveForLater.bind(this);
		this.newSubmission = this.newSubmission.bind(this);
		this.onRowAction = this.onRowAction.bind(this);
		this.processTabs = memoizeOne(this._processTabs.bind(this));
		this.onFileUploadRequested = this.onFileUploadRequested.bind(this);
		this.showCancelDialog = this.showCancelDialog.bind(this);
		this.closeReadOnlyDetail = this.closeReadOnlyDetail.bind(this);
		this.downloadFile = this.downloadFile.bind(this);
		this.customResultProps = Object.assign({}, customResultProps);
		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
		this.customResultProps.toolBar =
			<Icon
				name="refresh"
				active
				hint={getPhrase('refresh')}
				onMouseDown={async () => {
					this.setModel(await this.getModel().refreshSearch(true));
				}}
			/>;
	}

	showCancelDialog(submission) {
		this.setModel(this.getModel().showCancelDialog(submission));
	}

	closeReadOnlyDetail() {
		this.closeTab('rDetail');
	}

	async downloadFile(file) {
		const uf = await webapi.execute('/api/submission/getAttachmentDownloadInfo', {
			submissionId: this.getModel().getDisplayedSubmissionId(),
			seqId: file.get('seqId'),
			fileId: file.get('fileId')
		});
		if (uf === REPORTED_EXCEPTION) {
			return;
		}
		downloadFile(uf.fileId, uf.originalFileName);
	}

	renderFiles(files, readOnly) {
		return (
			<FileList
				files={files}
				onDownloadRequested={this.downloadFile}
				onDeleteRequested={readOnly ? null : (file, index) => {
					this.setModel(this.getModel().toggleFileDeleted(index));
				}}
			/>
		);
	}

	async onFileUploadRequested(files) {
		if (!files || files.length === 0) {
			return;
		}
		let model = this.getModel();
		const exFileSizeSum = model.getFileSizeSum();
		let newFileSizeSum = 0;
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			newFileSizeSum += file.size;
			console.log(file);
			if (file.size > singleFileSizeLimit) {
				showMessageBox(getPhrase('singleFileSizeLimitExceeded').replace('$limit', formatFileSize(singleFileSizeLimit)).replace('$fileName', file.name));
				return;
			}
		}

		console.log('new = ', newFileSizeSum, 'ex = ', exFileSizeSum);

		if (newFileSizeSum + exFileSizeSum > totalFileSizeLimit) {
			showMessageBox(getPhrase('fileSizeSumLimitExceeded').replace('$limit', formatFileSize(totalFileSizeLimit).replace('$size', formatFileSize(newFileSizeSum + exFileSizeSum))));
			return;
		}

		let newFiles = [];
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			const uploadedFile = await webapi.upload('/api/file', file);
			if (uploadedFile === REPORTED_EXCEPTION) {
				continue;
			}
			newFiles.push(Object.assign({
				size: file.size,
				fileName: uploadedFile.originalFileName
			}, uploadedFile));
		}
		this.setModel(model.addFiles(newFiles));
	}

	closeTab(tabId) {
		let model = this.getModel();
		if (tabId === 'wDetail' && model.isWDetailModified()) {
			model = model.showCloseUnsavedDialog();
		} else {
			model = model.closeTab(tabId);
		}
		this.setModel(model);
	}

	_processTabs(tabs) {
		return tabs.map(tab => {
			const tabId = tab.get('id');
			let text = tab.get('text');
			if (tabId !== 'list') {
				text =
					<TabText>
						<Icon
							name={tabId}
							className="left"
						/>
						{text}
						<Icon
							name="close"
							active
							onMouseDown={e => {
								e.preventDefault();
								window.setTimeout(() => {
									this.closeTab(tabId);
								}, 0);
							}}
						/>
					</TabText>;

			}
			return tab.set('text', text);
		});
	}

	async openDetail(id) {
		let model = this.getModel();
		if (model.getWDetailId() === id) {
			model = model.setSelectedTab('wDetail');
		} else {
			if (model.isWDetailModified()) {
				let status = await webapi.execute('/api/proc/api_submission/getStatus', {
					submissionId: id
				});
				if (status === REPORTED_EXCEPTION) {
					return;
				}
				model = model.showOpenOverUnsavedDialog(id);
			} else {
				model = await model.openDetail(id);
			}
		}
		this.setModel(model);
	}

	async onRowAction(row, rowIndex, options) {
		const id = row.get('id');
		this.openDetail(id);
	}

	async newSubmission() {
		this.openDetail(null);
	}

	async submit() {
		this.setModel(await this.getModel().saveSubmission(true));
	}

	async saveForLater() {
		this.setModel(await this.getModel().saveSubmission());
	}

	async resolveOpenOverModified(doSave) {
		const data = this.getData();
		const openOverUnsavedDialog = data.get('openOverUnsavedDialog');
		if (!openOverUnsavedDialog) {
			return;
		}
		let model = this.getModel();
		model = model.hideOpenOverUnsavedDialog();
		if (doSave) {
			model = await model.saveSubmission(false, true);
		}
		model = await model.openDetail(openOverUnsavedDialog.get('newId'));
		this.setModel(model);
		if (doSave) {
			updateEntityTimestamp('submission');
		}
	}

	onTabSelected(tabId) {
		this.setModel(this.getModel().setSelectedTab(tabId));
	}

	setComments(value) {
		this.setModel(this.getModel().setComments(value));
	}

	setRoleId(value) {
		this.setModel(this.getModel().setRoleId(value));
	}

	setTypeId(value) {
		this.setModel(this.getModel().setTypeId(value));
	}

	setName(value) {
		this.setModel(this.getModel().setName(value));
	}

	getModel() {
		return new SubmissionModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	async componentDidMount() {
		if (!this.getData().get('initialized')) {
			this.setModel(await this.getModel().initialize());
		}
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	setPersonalDataItem(name, value) {

	}

	textItem(pd, cpd, itemName, maxLength) {
		return (
			<FormItem label={getFormItemLabel(itemName, 'party')}>
				<TextInput
					value={pd.get(itemName) || ''}
					placeholder={cpd.get(itemName)}
					onChange={value => this.setPersonalDataItem(itemName, value)}
					changeOnBlur
					maxLength={maxLength}
				/>
			</FormItem>
		);
	}

	lookupItem(pd, cpd, itemName, lookup) {
		return (
			<FormItem label={getFormItemLabel(itemName, 'party')}>
				<LookupInput
					value={pd.get(itemName) || ''}
					placeholder={cpd.get(itemName)}
					onChange={value => this.setPersonalDataItem(itemName, value)}
					getRows={lookup.getRows}
					getText={lookup.getText}
				/>
			</FormItem>
		);
	}

	renderPersonalDataEditItems() {
		const pd = this.getData().get('personalData') || EMPTY_MAP;
		const cpd = this.getData().get('currentPersonalData') || EMPTY_MAP;
		const luBoolean = getLookup('ynBoolean');


		return (
			<>
				{this.textItem(pd, cpd, 'name', 250)}
				{this.textItem(pd, cpd, 'email', 50)}
				{this.textItem(pd, cpd, 'contactPhone', 50)}
				{this.textItem(pd, cpd, 'portalPhone', 50)}
				{this.textItem(pd, cpd, 'bankAccount', 50)}
			</>
		);
	}

	renderEditForm() {
		const data = this.getData();
		const detail = data.get('wDetail') || EMPTY_MAP;
		const roleLookup = getLookup('submissionPartyRole');
		const roleId = detail.get('roleId');
		const typeLookup = getLookup('submissionType.' + getRoleTypeId(roleId));
		const statusId = detail.get('statusId');
		const typeId = detail.get('typeId');
		return (
			<Content>
				<FlexBox>
					<FlexBox flexGrow="1">
						<FormWrapper>
							<Form vertical>
								<FormItem label={getFormItemLabel('name', 'submission')}>
									<TextInput
										value={detail.get('name')}
										onChange={this.setName}
										changeOnBlur
										maxLength="200"
									/>
								</FormItem>
								<FormItem label={getFormItemLabel('role_id', 'submission')}>
									<LookupInput
										value={roleId}
										onChange={this.setRoleId}
										getRows={roleLookup.getRows}
										getText={roleLookup.getText}
										mandatory
										combo
									/>
								</FormItem>
								<FormItem label={getFormItemLabel('type', 'submission')}>
									<LookupInput
										value={typeId}
										onChange={this.setTypeId}
										getRows={typeLookup.getRows}
										getText={typeLookup.getText}
										mandatory
										combo
									/>
								</FormItem>
								{
									typeId === 'PERSONAL_DATA' && false && this.renderPersonalDataEditItems()
								}
								<FormItem label={getFormItemLabel('comments', 'submission')}>
									<TextInput
										value={detail.get('comments')}
										onChange={this.setComments}
										placeholder={getPhrase('attachANoteOrAFile')}
										multiline
										changeOnBlur
										maxLength="1000"
										minHeight="160px"
									/>
								</FormItem>
								<Label>{getFormItemLabel('attachments', 'submission')}<span className='hint'>({getPhrase('submissionMaxAttachmentSize').replace('$singleFileSizeLimit', toMB(singleFileSizeLimit)).replace('$totalSizeLimit', toMB(totalFileSizeLimit))})</span></Label>
								<UploadPane
									onFileUploadRequested={this.onFileUploadRequested}
									buttonText={getPhrase('selectAttachment')}
									dragText={getPhrase('attachANoteOrAFile')}
									multiple={true}
									marginBottom="24px"
								/>
								{this.renderFiles(detail.get('files'))}
								<Buttons>
									<Button
										text={getPhrase('fileSubmission')}
										onAction={this.submit}
									/>
									<Button
										text={getPhrase('saveForLater')}
										type="medium"
										onAction={this.saveForLater}
									/>
									{
										statusId === 'SAVED_FOR_LATER' &&
										<Button
											text={getPhrase('cancelSubmission')}
											type="light"
											negative
											onAction={() => this.showCancelDialog(detail)}
										/>

									}
								</Buttons>
							</Form>
						</FormWrapper>
					</FlexBox>
					<FlexBox flexGrow="1">
						<StatusChangeHistory
							transitions={detail.get('transitions')}
							withDiffs
						/>
					</FlexBox>
				</FlexBox>
			</Content>
		);
	}

	renderReadOnlyDetail() {
		const data = this.getData();
		const detail = data.get('rDetail') || EMPTY_MAP;
		const statusId = detail.get('statusId');
		return (
			<Content>
				<FlexBox>
					<FlexBoxBlock flexGrow="1">
						<ReadOnlyDetail
							detail={detail}
							onFileDownloadRequested={this.downloadFile}
							buttons={(
								<>
									<Button
										text={getPhrase('backToOverview')}
										onAction={this.closeReadOnlyDetail}
									/>
									{
										statusId === 'SUBMITTED' &&
										<Button
											text={getPhrase('cancelSubmission')}
											type="light"
											negative
											onAction={() => this.showCancelDialog(detail)}
										/>

									}
								</>
							)}
						/>
					</FlexBoxBlock>
					<FlexBoxBlock flexGrow="1">
						<StatusChangeHistory
							transitions={detail.get('transitions')}
							withDiffs
						/>
					</FlexBoxBlock>
				</FlexBox>
			</Content>
		);
	}

	async setStatusGroupFilter(key, value) {
		this.setModel(await this.getModel().setStatusGroupFilter(key, value));
	}

	renderList() {
		const data = this.getData();
		const statusGroupFilter = data.get('statusGroupFilter') || EMPTY_MAP;
		return (
			<Content>
				<RowCountEtc>
					<RowCount>
						<span className="label">{getPhrase('submissionCount')}:</span>
						<span className="value">{data.getIn([
							'search',
							'data',
							'result',
							'data',
							'pager',
							'totalCount'
						])}</span>
					</RowCount>
					<Button
						type="full"
						iconName="add"
						text={getPhrase('newSubmission')}
						onAction={this.newSubmission}
					/>
				</RowCountEtc>
				<QuickFilter>
					<QuickFilterHeader>{getPhrase('displayedSubmissions')}</QuickFilterHeader>
					{
						STATUS_GROUPS.map(key => {
							return (
								<CheckButton
									key={key}
									text={getPhrase('submissionStatusGroup.' + key)}
									value={statusGroupFilter.get(key)}
									onChange={value => this.setStatusGroupFilter(key, value)}
								/>
							);
						})
					}

				</QuickFilter>
				<Module
					appState={data.get('search')}
					onDataChange={newData => {
						this.setData(data.setIn(['search', 'data'], newData));
					}}
					customResultProps={this.customResultProps}
					onRowAction={this.onRowAction}
				//leftToolBar={this.renderCandidateListToolbar}
				//onResultIconMouseDown={this.onCandidateIconMouseDown}
				/>
			</Content>
		);
	}

	renderDialogSubmission(detail) {
		if (!detail) {
			return null;
		}
		const roleId = detail.get('roleId');
		const typeLookup = getLookup('submissionType.' + getRoleTypeId(roleId));
		const statusId = detail.get('statusId');
		const status = detail.get('status');
		return (
			<DialogSubmission>
				<div className="type">
					{typeLookup.getText(detail.get('typeId'))}
				</div>
				<div className="createdWhen">{DateUtils.formatDate(detail.get('submittedWhen') || detail.get('createdWhen'), false)}</div>
				<div className="name">
					{getDetailTabHeader(detail)}
				</div>
				{
					statusId &&
					<StatusBubble className={statusId}>{status}</StatusBubble>

				}
			</DialogSubmission>
		);
	}

	renderOpenOverUnsavedDialog() {
		return (
			<Dialog
				onCloseRequested={() => {
					this.setModel(this.getModel().hideOpenOverUnsavedDialog());
				}}
			>
				<DialogHeader>{getPhrase('doYouWantToSaveOpenSubmission')}</DialogHeader>
				<DialogContent>
					{this.renderDialogSubmission(this.getData().get('wDetail'))}
				</DialogContent>
				<DialogSubtext>
					{getPhrase('anotherModifiedSubmission')}
				</DialogSubtext>
				<DialogButtons className="vertical">
					<Button
						text={getPhrase('saveAndOpenNew')}
						onAction={() => {
							this.resolveOpenOverModified(true);
						}}
					/>
					<Button
						text={getPhrase('openWithoutSaving')}
						type="medium"
						onAction={() => {
							this.resolveOpenOverModified(false);
						}}
					/>
				</DialogButtons>
			</Dialog>
		);
	}

	renderCloseUnsavedDialog() {
		return (
			<Dialog
				onCloseRequested={() => {
					this.setModel(this.getModel().hideCloseUnsavedDialog());
				}}
			>
				<DialogHeader>{getPhrase('doYouWantToSaveOpenSubmission')}</DialogHeader>
				<DialogContent>
					{this.renderDialogSubmission(this.getData().get('wDetail'))}
				</DialogContent>
				<DialogSubtext>
					{getPhrase('youHaveMadeSubmissionChanges')}
				</DialogSubtext>
				<DialogButtons className="vertical">
					<Button
						text={getPhrase('saveAndClose')}
						onAction={async () => {
							let model = this.getModel();
							await model.saveSubmission(false, true);
							model = model.closeTab('wDetail');
							model = model.hideCloseUnsavedDialog();
							this.setModel(model);
							window.setTimeout(() => {
								updateEntityTimestamp('submission');
							}, 0);
						}}
					/>
					<Button
						text={getPhrase('closeWithoutSaving')}
						type="medium"
						onAction={() => {
							this.setModel(this.getModel().hideCloseUnsavedDialog().closeTab('wDetail'));
						}}
					/>
				</DialogButtons>
			</Dialog>
		);
	}

	renderCancelDialog() {
		const data = this.getData();
		const detail = data.get('cancelDialog');
		return (
			<Dialog
				onCloseRequested={() => {
					this.setModel(this.getModel().hideCancelDialog());
				}}
			>
				<DialogHeader>{getPhrase('reallyCancelSubmission')}</DialogHeader>
				<DialogContent>
					{this.renderDialogSubmission(detail)}
				</DialogContent>
				<DialogButtons>
					<Button
						text={getPhrase('doCancelSubmission')}
						negative
						onAction={async () => {
							let model = this.getModel();
							const res = await webapi.execute('/api/proc/api_submission/setStatus', {
								submissionId: detail.get('id'),
								statusId: 'CANCELLED'
							});
							if (res === REPORTED_EXCEPTION) {
								return;
							}
							model = model.closeSelectedTab();
							model = model.hideCancelDialog();
							this.setModel(model);
							window.setTimeout(() => {
								updateEntityTimestamp('submission');
							}, 0);
						}}
					/>
					<Button
						text={getPhrase('close')}
						type="medium"
						onAction={() => {
							this.setModel(this.getModel().hideCancelDialog());
						}}
					/>
				</DialogButtons>
			</Dialog>
		);
	}

	render() {
		const data = this.getData();
		const tabs = this.processTabs(data.get('tabs') || EMPTY_LIST);
		const selectedTabId = data.get('selectedTabId') || 'list';
		const openOverUnsavedDialog = data.get('openOverUnsavedDialog');
		const closeUnsavedDialog = data.get('closeUnsavedDialog');
		const cancelDialog = data.get('cancelDialog');
		return (
			<Wrapper>
				<ModuleHeaderSection className="noBorder">
					<ModuleHeaderBlock>
						<ModuleHeader>{getPhrase('Submission')}</ModuleHeader>
					</ModuleHeaderBlock>
					<ModuleHeaderBlock>
						<ModuleLogo />
					</ModuleHeaderBlock>
				</ModuleHeaderSection>
				<ModuleBodySection top="-60px">
					<TabPane
						tabs={tabs}
						selectedTabId={selectedTabId}
						onTabSelected={this.onTabSelected}
					/>
					{
						selectedTabId === 'list' ? this.renderList() :
							selectedTabId === 'wDetail' ? this.renderEditForm() :
								this.renderReadOnlyDetail()
					}
				</ModuleBodySection>
				{
					!!openOverUnsavedDialog && this.renderOpenOverUnsavedDialog()
				}
				{
					!!closeUnsavedDialog && this.renderCloseUnsavedDialog()
				}
				{
					!!cancelDialog && this.renderCancelDialog()
				}
			</Wrapper>
		);
	}
}

const cellRendererMap = Object.freeze({
	'name': (cell, row, colIndex) => {
		return (
			<CellMainText className={row.get('status_id') === 'CANCELLED' ? 'deleted' : ''}>{cell}</CellMainText>
		);
	},
	'role_id': (cell, row, colIndex) => {
		return (
			<>
				<CellMainText className="secondary">{cell}</CellMainText>
				<CellSubText>{row.get('role_type')}</CellSubText>
			</>
		);
	},
	'status': (cell, row) => {
		return (
			<StatusBubble className={row.get('status_id')}>{cell}</StatusBubble>
		);
	},
	last_modified: renderDateTimeCell,
	'submitted_when': (cell, row, colIndex) => {
		if (cell) {
			return renderDateTimeCell(cell, row, colIndex);
		} else {
			return (
				<i>
					{renderDateTimeCell(DateUtils.formatDate(row.get('created_when')), row, colIndex)}
				</i>
			);
		}
	}
});

const customResultProps = {
	cellRendererMap,
	rowCountLabel: () => getPhrase('submissionCount'),
	defaultColumnWidths: {
		'submitted_when': '120px',
		last_modified: '120px',
		'status': '180px',
		type: '240px',
	},
	columnFilters: {
		'status': {
			type: 'lookup',
			lookup: 'submissionStatus'
		},
		'type': {
			type: 'lookup',
			lookup: 'submissionType'
		},
		'submitted_when': {
			type: 'date'
		},
		last_modified: {
			type: 'date'
		},
		name: {
			type: 'text'
		},
		role_id: {
			type: 'lookup',
			lookup: 'submissionPartyRole'
		},
	}
};

export default withGlobalContext(SubmissionModule);
